import { useRef } from 'react';
import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';

/**
 * The quotation mark the hand-off sets behind each quote: two slabs, 167x119. Inlined rather than
 * shipped as a file — it is one path, and inlining lets the colour come from the card so the light
 * and dark variants can tint it differently instead of shipping two assets.
 */
function QuoteMark({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 167 119"
			className={cn('h-[119px] w-[167px] shrink-0', className)}
			fill="currentColor"
			aria-hidden
		>
			<path d="M84.5 119L132 0H167L139 119H84.5ZM0 119L47 0H82L54.5 119H0Z" />
		</svg>
	);
}

type Testimonial = { quote: string; name: string; role: string };

/**
 * The UI Kit's `Card-testimonio` (`45:973`) ships two colourways, and they are the DEFAULT and HOVER
 * states of one card — not two variants to alternate between. Resting it is cream with ink type;
 * pointed at, the whole card flips to ink with the quote in olive.
 *
 * `focus-within` rides along with `hover` so the same state is reachable from the keyboard, and every
 * colour is declared on both sides of the pair — a `group-hover` that only names the dark value
 * leaves the light one to be inherited, which is what makes these flips drift.
 */
function TestimonialCard({ item }: { item: Testimonial }) {
	return (
		<figure
			className={cn(
				// The width comes from the strip's `--card`, not from a literal here: the strip needs the
				// same number to work out how far it must be able to scroll, and two copies of it would
				// drift the day the card is resized.
				'group relative flex h-[528px] w-[var(--card)] shrink-0 snap-start flex-col justify-between overflow-hidden rounded-[2rem] p-8 transition-colors duration-300',
				'md:h-[454px] md:p-10',
				'bg-sand hover:bg-ink focus-within:bg-ink',
			)}
		>
			{/* The hand-off places the mark at (24,24) and starts the quote at (40,40) — they OVERLAP, so
			    this is a watermark behind the text, not a glyph stacked above it. */}
			<div aria-hidden className="pointer-events-none absolute top-4 left-4 md:top-6 md:left-6">
				<QuoteMark className="text-brand/10 group-focus-within:text-white/10 group-hover:text-white/10" />
			</div>

			{/* The typographic quotes are the COMPONENT's, not the copy's: a translator given a msgid that
			    already carries them drops one as often as not — which is how the live review found a quote
			    closed but never opened. The pair is drawn here so every card, in every locale, gets both. */}
			<blockquote className="text-ink group-hover:text-olive group-focus-within:text-olive relative max-w-[482px] text-xl leading-[1.3] font-medium tracking-[-0.02em] transition-colors duration-300 md:text-2xl">
				“{item.quote}”
			</blockquote>

			<figcaption className="relative mt-8 flex flex-col leading-[1.5]">
				<span className="text-ink text-base font-medium tracking-[-0.02em] transition-colors duration-300 group-focus-within:text-white group-hover:text-white">
					{item.name}
				</span>
				<span className="text-ink/50 text-sm tracking-[-0.02em] transition-colors duration-300 group-focus-within:text-white/50 group-hover:text-white/50">
					{item.role}
				</span>
			</figcaption>
		</figure>
	);
}

/**
 * "Qué dicen de nosotros" — partner quotes, on the same cream band as the directory above.
 *
 * A scroll-snap strip rather than a JS carousel: three cards that all fit at the hand-off's width and
 * simply overflow below it, which is what the arrows are for. Matches how `success-cases` on the
 * Solutions page and the homepage's project strip already behave.
 */
export function Testimonials() {
	const { t } = useLingui();
	const scrollerRef = useRef<HTMLDivElement>(null);

	const scrollByCard = (direction: 1 | -1) => {
		const scroller = scrollerRef.current;
		const card = scroller?.firstElementChild;
		// Step by the card AND the gap the strip is actually rendering — 327/16 on a phone, 562/24 above
		// `md` — so that one click lands exactly on the next card's snap position. Hardcoding either
		// number overshoots on one of the two, and the strip then rests a few pixels off the gutter.
		const gap = scroller ? Number.parseFloat(getComputedStyle(scroller).columnGap) || 0 : 0;
		const step = card ? card.getBoundingClientRect().width + gap : 360;
		scroller?.scrollBy({ left: direction * step, behavior: 'smooth' });
	};

	const testimonials: Testimonial[] = [
		{
			quote: t`The impact of working with Worldcoo has been €392,000, which has let us cover more than 130,000 meals in our School Meal Grants programme for children in vulnerable situations`,
			name: 'Elisenda González',
			role: t`Child and adolescent protection specialist at Educo.`,
		},
		{
			quote: t`When we discovered the Solidarity Round-Up programme we thought it was a fantastic initiative. It lets society get involved in caring for the sea and the environment in a much more direct way. With very little, they can help collect huge amounts of waste from the beaches`,
			name: 'Gastón Cedrone',
			role: t`Head of Projects at Fundación Ecomar.`,
		},
		{
			// Lifted verbatim from the current landing page's testimonial carousel, msgids and all, so it
			// is a quote the Red Cross actually gave us and it arrives already translated into all nine
			// locales. It replaced the quote the hand-off drew here, which repeated ONE quote across two
			// different people and so gave this card no real copy to take.
			quote: t`With Charity Rounding Up we have been able to give advice for emotional well-being and psychosocial support to people in vulnerable situations affected by Covid.`,
			name: 'Jaime Gregori',
			role: t`Director of Fundraising, corporate alliances and social responsibility at Spanish Red Cross`,
		},
		// The three below speak about the model rather than about a project of their own, which is why
		// they come after the partner entities.
		{
			quote: t`A very interesting and encouraging format. Instead of a fixed monthly fee, it gives the user complete freedom to contribute to a social project whenever they want. It also democratizes participation, with amounts accessible to everyone.`,
			name: 'Pau Gasol',
			role: t`NBA Player and Co-Founder of the Gasol Foundation`,
		},
		{
			quote: t`For me, Charity Rounding Up is a message of support that thousands of people send to those women who have been, or still are, mistreated by their partners: telling them that they are not alone and that, with that donation, they can carry on and they deserve to be happy.`,
			name: 'Ana Bella',
			role: t`Founder of Fundación Ana Bella`,
		},
		{
			quote: t`A very good initiative because, with little effort, a person feels they are contributing to a good cause. It also says a lot about the values of the company and the people who collaborate.`,
			name: 'Cristina Maragall',
			role: t`Spokesperson for the Pasqual Maragall Foundation`,
		},
	];

	return (
		<section className="bg-blush overflow-hidden py-12 md:py-24">
			<div className="mx-auto max-w-[1440px] px-6 xl:px-[54px]">
				{/* Mobile stacks the arrows UNDER the heading — at 343px the heading wraps to three lines and
				    leaves nothing beside it. Above `lg` they sit on the same line, as the desktop frame draws. */}
				<div className="flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
					<h2 className="text-3xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
						{t`What they say about us`}
					</h2>
					<div className="flex shrink-0 gap-2">
						<button
							type="button"
							onClick={() => scrollByCard(-1)}
							aria-label={t`Previous testimonial`}
							className="border-ink text-ink hover:bg-ink/5 grid size-8 cursor-pointer place-items-center rounded-full border transition-colors"
						>
							<ArrowLeft className="size-4" />
						</button>
						<button
							type="button"
							onClick={() => scrollByCard(1)}
							aria-label={t`Next testimonial`}
							className="border-ink text-ink hover:bg-ink/5 grid size-8 cursor-pointer place-items-center rounded-full border transition-colors"
						>
							<ArrowRight className="size-4" />
						</button>
					</div>
				</div>
			</div>

			{/* Bleeds off the RIGHT edge only. The first card lines up with the heading above it — the
			    page's max-width container plus the hand-off's own gutter — while the strip runs on to the
			    window edge, so a card continuing past the fold reads as "there is more here" rather than
			    as something a container clipped.

			    `--gutter` is therefore NOT the container's own padding: it is the distance from the WINDOW
			    edge to where the container's text begins, which above 1440px is half the leftover width
			    plus that gutter. `max()` keeps it at the plain gutter below 1440. The gutter is the HERO's
			    (54px), not the 32px the hand-off draws for this section: the first live review read the 32
			    as the block having no left margin at all next to its neighbours. Keep it in step with the
			    heading above — it is the same pair of values as that container's `px`.

			    Three properties then hang off that one value, and the LEFT edge of the viewport is what
			    each of them is protecting:

			      · `pl` is the resting inset, which is what puts card one under the heading.
			      · `scroll-pl` moves the snap edge inwards. A snap container aligns `snap-start` to the
			        SCROLLPORT edge, so padding alone lets the browser scroll the gutter out of view — and
			        it does, when it settles the strip on load.
			      · `pr` is the room the LAST card needs to be able to reach that same gutter: a viewport
			        less the gutter and one card. It is what makes the end of the scroll a gutter-aligned
			        card rather than an arbitrary offset — without it the strip runs out of scroll
			        mid-card, and that WAS the defect the live review reported here: one arrow click ran
			        the strip to the end and left the first card sliced off by the window edge.

			    Snapping stays `snap-x` — proximity, as the homepage's two strips also use — and is only
			    there to tidy up a swipe. It is not what aligns the arrows: with `pr` in place the stride
			    below already lands exactly on a card, at all three widths (343 on a phone, 586 above `md`,
			    and the last page is the end of the scroll to the pixel). Mandatory would only add a second
			    opinion about where a programmatic scroll may finish. */}
			<div
				ref={scrollerRef}
				className="hide-scrollbar mt-6 flex snap-x scroll-pl-[var(--gutter)] gap-4 overflow-x-auto scroll-smooth pr-[max(0px,calc(100%-var(--gutter)-var(--card)))] pb-2 pl-[var(--gutter)] [--card:327px] [--gutter:max(1.5rem,calc((100%-1440px)/2+1.5rem))] md:gap-6 md:[--card:562px] xl:[--gutter:max(3.375rem,calc((100%-1440px)/2+3.375rem))]"
			>
				{testimonials.map((item) => (
					<TestimonialCard key={item.name} item={item} />
				))}
			</div>
		</section>
	);
}
